import { createSelector } from "@reduxjs/toolkit";
import { Organization } from "@packages/client";

import { updateOrganizationRequest } from "Api/organizations/updateOrganization.request";
import logger from "Libs/logger";
import { selectCurrentUserUUID } from "Reducers/app/selectors";
import {
  getOrganization,
  getOrganizations
} from "Reducers/organization/organization";
import {
  getOrganizationTrial,
  organizationTrialSelector
} from "Reducers/organization/trial";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { organizationPricingModel } from "./profiles";

import type { RootState } from "Store/configureStore";
import type { ProfileDetail } from "Api/billingSystem/types";

const organizationMatchesPricingModel = (
  organization: Organization,
  pricingModel: ProfileDetail["pricing_model"]
) => organizationPricingModel(organization) === pricingModel;

/**
 * After a billing profile is created, attach an eligible organization to it. An
 * organization is eligible when the current user owns it, it's on the new
 * billing system (billing_legacy === false), it isn't already linked to a
 * billing profile, and it's on an active trial.
 *
 * When several organizations qualify (e.g. staff), the one that triggered the
 * flow / is in the URL is preferred (`priorityOrganizationId`); otherwise the
 * first eligible organization is used.
 */
export const attachEligibleOrganizationToBillingProfile = createAppAsyncThunk(
  "billingSystem/attachEligibleOrganizationToBillingProfile",
  async (
    {
      billingProfileId,
      priorityOrganizationId
    }: { billingProfileId: string; priorityOrganizationId?: string },
    { getState, dispatch }
  ) => {
    const currentUserUUID = selectCurrentUserUUID(getState());

    // Cheap checks first: owned, new-billing, not yet linked to a profile.
    const candidates = getOrganizations(getState()).filter(
      (organization): organization is Organization =>
        !!organization &&
        organization.owner_id === currentUserUUID &&
        organization.billing_legacy === false &&
        !organization.billing_profile_id
    );

    const isPriority = (organization: Organization) =>
      !!priorityOrganizationId &&
      (organization.id === priorityOrganizationId ||
        organization.name === priorityOrganizationId);

    // Check the prioritized org first; the sort is stable so the rest keep
    // their original order ("first eligible candidate").
    const ordered = [...candidates].sort(
      (a, b) => Number(isPriority(b)) - Number(isPriority(a))
    );

    // Attach the first one on an active trial. Trial status lives in its own
    // per-org slice (keyed by name); fetch it when we don't already have it.
    for (const organization of ordered) {
      let trial = organizationTrialSelector(getState(), {
        organizationId: organization.name
      });

      if (!trial) {
        trial = await dispatch(
          getOrganizationTrial({ organizationId: organization.name })
        )
          .unwrap()
          .catch(() => undefined);
      }

      if (trial?.status !== "active") continue;

      await updateOrganizationRequest(organization.id, {
        billing_profile_id: billingProfileId
      });

      // Refresh so the store reflects the new billing profile link before we
      // resolve (the wizard waits on this before closing/redirecting).
      await dispatch(
        getOrganization({ organizationDescriptionId: organization.name })
      );

      return organization.id;
    }

    return undefined;
  }
);

/**
 * Organizations the user can link to a billing profile from the wizard's
 * "Link organizations" step: ones they own, on the new billing system
 * (billing_legacy === false), not yet linked to a billing profile, and — when
 * the profile's `pricingModel` is known — of a matching pricing kind (a fixed
 * org can't join a flex profile and vice-versa). Trial status is intentionally
 * NOT part of this — that only gates the single-org auto-upgrade in
 * attachEligibleOrganizationToBillingProfile.
 */
export const selectEligibleOrganizationsForBillingProfile = createSelector(
  // Wrapped in an arrow so the reference resolves at call time — `getOrganizations`
  // and this module sit in a circular import, so referencing it directly here
  // can be undefined at module-evaluation time.
  (state: RootState) => getOrganizations(state),
  selectCurrentUserUUID,
  (_: RootState, pricingModel?: ProfileDetail["pricing_model"]) => pricingModel,
  (organizations, currentUserUUID, pricingModel) =>
    organizations.filter((organization): organization is Organization => {
      if (!organization) return false;

      const isOwnedByUser = organization.owner_id === currentUserUUID;
      const isOnNewBillingSystem = organization.billing_legacy === false;
      const isNotLinkedToProfile = !organization.billing_profile_id;
      // Only enforce the pricing match once the profile's model is known; a
      // fixed org can't join a flex profile and vice-versa.
      const matchesProfilePricing =
        !pricingModel ||
        organizationMatchesPricingModel(organization, pricingModel);

      return (
        isOwnedByUser &&
        isOnNewBillingSystem &&
        isNotLinkedToProfile &&
        matchesProfilePricing
      );
    })
);

/**
 * Explicitly link the organizations the user selected on the wizard step to the
 * newly created billing profile, one PATCH per organization, then refresh each
 * so the store reflects the new link.
 */
export const attachOrganizationsToBillingProfile = createAppAsyncThunk(
  "billingSystem/attachOrganizationsToBillingProfile",
  async (
    {
      billingProfileId,
      organizationIds
    }: { billingProfileId: string; organizationIds: string[] },
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const organizationsById: Record<string, Organization> = {};
      getOrganizations(getState()).forEach(organization => {
        if (organization) organizationsById[organization.id] = organization;
      });

      await Promise.all(
        organizationIds.map(id =>
          updateOrganizationRequest(id, {
            billing_profile_id: billingProfileId
          })
        )
      );

      // Refreshing is best-effort: the links are already persisted, so a failed
      // refetch must not surface as a failed link.
      await Promise.all(
        organizationIds.map(id => {
          const organization = organizationsById[id];
          return organization
            ? dispatch(
                getOrganization({
                  organizationDescriptionId: organization.name
                })
              )
                .unwrap()
                .catch(() => undefined)
            : Promise.resolve();
        })
      );

      return organizationIds;
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);
